/**
 * CMS-10 "Usuarios y accesos" (docs/06 §2.9): the search results on the left, the detail on the
 * right — profile, role select (admin only, audited), block, access status, the subscriptions
 * behind it, grant redemptions, sessions hosted/joined and the devices used.
 *
 * Phase 1 (docs/07 §1) access is all-or-nothing and comes from a grant, so the panel is read-only
 * and there is no "add manual subscription" (phase 2 with the rest of billing).
 */
import {
  type CmsUserDetailResponse,
  type CmsUserDTO,
  type CmsUsersQueryInput,
  type CmsUsersResponse,
  type Locale,
  pickI18n,
  type Role,
} from '@akpl/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../api';
import { atLeast, useAuth } from '../auth/AuthProvider';
import { ExportCsvButton } from '../components/data-table/ExportCsvButton';
import { ListError } from '../components/data-table/ListError';
import { PageHeader } from '../components/layout/AppShell';
import { resultsAnchorProps, useResultsAnchor } from '../components/layout/resultsAnchor';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { Field, Input, Select } from '../components/ui/field';
import { Badge, Panel, PanelHeader } from '../components/ui/panel';
import { useT } from '../i18n';
import { cn } from '../lib/cn';
import type { CsvColumn } from '../lib/csv';
import { fetchAllRowsResult } from '../lib/exportAll';
import { formatDateTime, formatNumber } from '../lib/format';

const ROLES: Role[] = ['member', 'viewer', 'editor', 'admin'];

/** CMS-21: the operator can take the user list out of the console (docs/06 §4). */
export const userCsvColumns: CsvColumn<CmsUserDTO>[] = [
  { header: 'uid', value: (u) => u.id },
  { header: 'email', value: (u) => u.email },
  { header: 'display_name', value: (u) => u.displayName },
  { header: 'role', value: (u) => u.role },
  { header: 'is_anonymous', value: (u) => u.isAnonymous },
  { header: 'blocked', value: (u) => u.blocked },
  { header: 'locale', value: (u) => u.locale },
  { header: 'has_access', value: (u) => u.access.hasAccess },
  { header: 'access_until', value: (u) => u.access.until },
  { header: 'last_login_at', value: (u) => u.lastLoginAt },
  { header: 'created_at', value: (u) => u.createdAt },
];

export function toUsersQuery(q: string): CmsUsersQueryInput {
  const query: CmsUsersQueryInput = { page: 1, pageSize: 50 };
  if (q.trim()) query.q = q.trim();
  return query;
}

/** "Acceso: activo hasta …" / "Acceso: sin vencimiento" / "Sin acceso" of CMS-10. */
export function accessLabel(
  access: CmsUserDTO['access'],
  t: (key: string, opts?: Record<string, unknown>) => string,
  locale: string,
): string {
  if (!access.hasAccess) return t('cms:users.access.none');
  if (access.until === null) return t('cms:users.access.perpetual');
  return t('cms:users.access.activeUntil', { date: formatDateTime(access.until, locale) });
}

function initials(user: CmsUserDTO): string {
  const source = (user.displayName ?? user.email ?? user.id).trim();
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').toUpperCase();
}

function ResultRow({
  user,
  selected,
  onSelect,
}: {
  user: CmsUserDTO;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useT();
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected}
        className={cn(
          'flex w-full items-center gap-3 border-b border-line px-4 py-2.5 text-left last:border-0',
          selected ? 'bg-pink/6' : 'hover:bg-canvas',
        )}
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-cyan/25 text-[13px] font-bold text-ink">
          {initials(user)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-ink">
            {user.displayName ?? user.email ?? user.id}
          </span>
          <span className="block truncate text-[12px] text-muted">
            {user.isAnonymous ? t('cms:users.guest') : (user.email ?? user.id)}
          </span>
        </span>
        {user.blocked && <Badge tone="danger">{t('cms:users.blocked')}</Badge>}
        <Badge tone={user.role === 'member' ? 'neutral' : 'cyan'}>
          {t(`cms:roles.${user.role}` as 'cms:roles.member')}
        </Badge>
      </button>
    </li>
  );
}

function UserDetail({ uid }: { uid: string }) {
  const { t, i18n } = useT();
  const locale = i18n.language;
  /** CMS-46: the product name follows the console language, it is not always the ES branch. */
  const uiLocale: Locale = i18n.language === 'en' ? 'en' : 'es';
  const { role: myRole } = useAuth();
  const isAdmin = atLeast(myRole, 'admin');
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery<CmsUserDetailResponse>({
    queryKey: ['cms', 'user', uid],
    queryFn: () => api.cmsUser({ params: { id: uid } }),
  });

  const confirm = useConfirm();

  const patch = useMutation({
    mutationFn: (body: { role?: Role; blocked?: boolean }) =>
      api.cmsUserPatch({ params: { id: uid }, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cms', 'user', uid] });
      void queryClient.invalidateQueries({ queryKey: ['cms', 'users'] });
    },
    onError: () => setError(t('common:states.error')),
  });

  if (detail.isLoading) {
    return <p className="p-6 text-[13px] text-muted">{t('common:states.loading')}</p>;
  }
  if (!detail.data) {
    return <p className="p-6 text-[13px] text-danger">{t('common:states.error')}</p>;
  }

  const { user, subscriptions, redemptions, sessions, devices } = detail.data;
  /** Blocking locks a person out of the app, so it asks first; unblocking does not (UX-211). */
  const onToggleBlock = async () => {
    if (!user.blocked) {
      const ok = await confirm({
        title: t('cms:confirm.blockUser.title', {
          name: user.displayName ?? user.email ?? user.id,
        }),
        body: t('cms:confirm.blockUser.body'),
        confirmLabel: t('cms:confirm.blockUser.confirm'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    patch.mutate({ blocked: !user.blocked });
  };
  const hosted = sessions.filter((session) => session.role === 'host').length;
  const joined = sessions.length - hosted;

  return (
    <div className="flex flex-col gap-4">
      <Panel>
        <PanelHeader
          title={user.displayName ?? user.email ?? user.id}
          subtitle={`${user.email ?? t('cms:users.guest')} · ${t('cms:users.meta', {
            uid: user.id,
            created: formatDateTime(user.createdAt, locale),
          })}`}
          action={
            isAdmin ? (
              <Button
                variant={user.blocked ? 'secondary' : 'danger'}
                size="sm"
                disabled={patch.isPending}
                onClick={() => void onToggleBlock()}
              >
                {user.blocked ? t('cms:users.unblock') : t('cms:users.block')}
              </Button>
            ) : null
          }
        />
        <div className="grid grid-cols-2 gap-4 px-4 py-3 md:grid-cols-4">
          <Field label={t('cms:users.role')}>
            {(id) => (
              <Select
                id={id}
                value={user.role}
                disabled={!isAdmin || patch.isPending}
                onChange={(e) => patch.mutate({ role: e.target.value as Role })}
              >
                {ROLES.map((value) => (
                  <option key={value} value={value}>
                    {t(`cms:roles.${value}` as 'cms:roles.member')}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('cms:users.locale')}>
            {(id) => <Input id={id} value={user.locale} readOnly disabled />}
          </Field>
          <Field label={t('cms:users.lastLogin')}>
            {(id) => (
              <Input
                id={id}
                value={
                  user.lastLoginAt ? formatDateTime(user.lastLoginAt, locale) : t('cms:users.never')
                }
                readOnly
                disabled
              />
            )}
          </Field>
          <Field label={t('cms:users.stripeCustomer')}>
            {(id) => <Input id={id} value={user.stripeCustomerId ?? '—'} readOnly disabled />}
          </Field>
        </div>
        {error && (
          <p className="px-4 pb-3 text-[12px] text-danger" role="alert">
            {error}
          </p>
        )}
      </Panel>

      <Panel>
        <PanelHeader
          title={t('cms:users.access.title')}
          subtitle={accessLabel(user.access, t as never, locale)}
        />
        {subscriptions.length > 0 ? (
          <table className="w-full border-collapse text-left text-[13px]">
            <thead>
              <tr className="border-b border-line text-[12px] font-semibold tracking-wide text-muted uppercase">
                <th scope="col" className="px-4 py-2">
                  {t('cms:users.access.product')}
                </th>
                <th scope="col" className="px-4 py-2">
                  {t('cms:users.access.source')}
                </th>
                <th scope="col" className="px-4 py-2">
                  {t('cms:users.access.status')}
                </th>
                <th scope="col" className="px-4 py-2">
                  {t('cms:users.access.period')}
                </th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((subscription) => (
                <tr key={subscription.id} className="border-b border-line last:border-0">
                  <td className="px-4 py-2 text-ink">
                    {pickI18n(subscription.productName, uiLocale) || subscription.productId}
                  </td>
                  <td className="px-4 py-2">
                    <Badge tone={subscription.source === 'grant' ? 'pink' : 'cyan'}>
                      {t(`cms:users.source.${subscription.source}` as 'cms:users.source.grant')}
                    </Badge>
                  </td>
                  <td className="px-4 py-2">
                    <Badge tone={subscription.status === 'active' ? 'ok' : 'neutral'}>
                      {t(`cms:status.${subscription.status}` as 'cms:status.active')}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-muted">
                    {subscription.currentPeriodEnd
                      ? t('cms:users.access.until', {
                          date: formatDateTime(subscription.currentPeriodEnd, locale),
                        })
                      : t('cms:users.access.noEnd')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="px-4 py-4">
            <p className="text-[13px] text-muted">{t('cms:users.access.empty')}</p>
            <p className="mt-1 text-[12px] text-muted">{t('cms:users.access.phase1')}</p>
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel data-testid="user-redemptions">
          <PanelHeader
            title={t('cms:users.redemptions.title')}
            subtitle={t('cms:users.redemptions.subtitle')}
          />
          {redemptions.length > 0 ? (
            <ul className="divide-y divide-line">
              {redemptions.map((redemption) => (
                <li key={redemption.id} className="px-4 py-2.5">
                  <p className="text-[13px] font-semibold break-all text-ink">
                    {redemption.matchValue ?? redemption.grantId}
                  </p>
                  <p className="text-[12px] text-muted">
                    {t('cms:grants.accessFull')} · {formatDateTime(redemption.redeemedAt, locale)}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-4 text-[13px] text-muted">{t('cms:users.redemptions.empty')}</p>
          )}
        </Panel>

        <Panel data-testid="user-sessions">
          <PanelHeader
            title={t('cms:users.sessions.title')}
            subtitle={t('cms:users.sessions.counts', { hosted, joined })}
          />
          {sessions.length > 0 ? (
            <ul className="divide-y divide-line">
              {sessions.slice(0, 8).map((session) => (
                <li key={session.id} className="px-4 py-2.5">
                  <p className="text-[13px] font-semibold text-ink">
                    {session.joinCode} · {t(`cms:status.${session.status}` as 'cms:status.active')}
                  </p>
                  <p className="text-[12px] text-muted">
                    {t('cms:users.sessions.songs', { count: session.songsPlayed })}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-4 text-[13px] text-muted">{t('cms:users.sessions.empty')}</p>
          )}
        </Panel>

        <Panel data-testid="user-devices">
          <PanelHeader
            title={t('cms:users.devices.title')}
            subtitle={t('cms:users.devices.used', { count: devices.length })}
          />
          {devices.length > 0 ? (
            <ul className="divide-y divide-line">
              {devices.map((device) => (
                <li key={device.id} className="px-4 py-2.5">
                  <p className="text-[13px] font-semibold break-all text-ink">{device.id}</p>
                  <p className="text-[12px] text-muted">
                    {device.model ?? '—'} · {device.platform}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-4 text-[13px] text-muted">{t('cms:users.devices.empty')}</p>
          )}
        </Panel>
      </div>
    </div>
  );
}

export function Users() {
  // UX-576: /users is a list too, so it gets the «Saltar a los resultados» link.
  useResultsAnchor();
  const { t, i18n } = useT();
  const locale = i18n.language;
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const query = toUsersQuery(q);
  const result = useQuery<CmsUsersResponse>({
    queryKey: ['cms', 'users', query],
    queryFn: () => api.cmsUsers({ query }),
    placeholderData: keepPreviousData,
  });

  const items = result.data?.items ?? [];
  const total = result.data?.total ?? 0;

  return (
    <>
      <PageHeader
        title={t('cms:users.title')}
        /**
         * No count until the query answers: `total ?? 0` claimed an empty collection for the
         * seconds the first page takes (QA 2026-09-15 UX-502, the UX-201 rule applied to the
         * screens it was missed on). An error degrades the count to "—".
         */
        subtitle={
          result.data !== undefined
            ? t('cms:users.subtitle', { count: total, total: formatNumber(total, locale) })
            : result.isError
              ? t('cms:users.subtitle', { count: 0, total: '—' })
              : t('cms:users.subtitleLoading')
        }
        actions={
          <span className="flex items-center gap-2">
            <ExportCsvButton
              filename="akapella-users.csv"
              columns={userCsvColumns}
              data-testid="users-export"
              // The whole filtered set, not the page on screen (CMS-22).
              rows={(onProgress) =>
                fetchAllRowsResult(
                  (page, pageSize) => api.cmsUsers({ query: { ...query, page, pageSize } }),
                  { onProgress },
                )
              }
            />
            <Input
              className="w-72"
              placeholder={t('cms:users.searchPlaceholder')}
              aria-label={t('cms:users.searchPlaceholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {/* UX-514: a way back from a search that returned nothing. */}
            <Button variant="link" size="sm" disabled={q === ''} onClick={() => setQ('')}>
              {t('cms:table.clearFilters')}
            </Button>
          </span>
        }
      />

      <div className="grid grid-cols-1 gap-4 px-6 pb-8 lg:grid-cols-[340px_minmax(0,1fr)]">
        {/* UX-576: «Saltar a los resultados» lands on this named list, not 18 Tabs away. */}
        <Panel className="self-start" {...resultsAnchorProps(t('cms:users.results'))}>
          <PanelHeader
            title={t('cms:users.results')}
            subtitle={t('cms:users.resultsCount', {
              shown: formatNumber(items.length, locale),
              total: formatNumber(total, locale),
            })}
          />
          {result.isError ? (
            <ListError onRetry={() => void result.refetch()} />
          ) : result.isLoading ? (
            <p className="px-4 py-4 text-[13px] text-muted">{t('common:states.loading')}</p>
          ) : items.length === 0 ? (
            <p className="px-4 py-4 text-[13px] text-muted">{t('cms:users.empty')}</p>
          ) : (
            <ul>
              {items.map((user) => (
                <ResultRow
                  key={user.id}
                  user={user}
                  selected={selected === user.id}
                  onSelect={() => setSelected(user.id)}
                />
              ))}
            </ul>
          )}
        </Panel>

        {selected ? (
          <UserDetail key={selected} uid={selected} />
        ) : (
          <Panel className="flex min-h-40 items-center justify-center">
            <p className="text-[13px] text-muted">{t('cms:users.pick')}</p>
          </Panel>
        )}
      </div>
    </>
  );
}
